// Lightweight screenshot using html2canvas loaded from CDN at capture-time,
// so it doesn't inflate the widget base bundle.

type Html2CanvasFn = (el: HTMLElement, opts?: Record<string, unknown>) => Promise<HTMLCanvasElement>;

async function loadHtml2Canvas(): Promise<Html2CanvasFn> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.html2canvas) return w.html2canvas;
  await new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("failed to load html2canvas"));
    document.head.appendChild(s);
  });
  return w.html2canvas;
}

async function presign(
  apiBase: string,
  appKey: string,
  contentType: string,
): Promise<{ url: string; key: string }> {
  const res = await fetch(`${apiBase}/v1/screenshot/presign`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Widget-Key": appKey },
    body: JSON.stringify({ contentType }),
  });
  if (!res.ok) throw new Error(`presign ${res.status}`);
  return res.json();
}

export async function captureScreenshot(apiBase: string, appKey: string): Promise<string | null> {
  try {
    const html2canvas = await loadHtml2Canvas();
    const canvas = await html2canvas(document.body, {
      logging: false,
      scale: Math.min(1, 1200 / Math.max(1, window.innerWidth)),
      useCORS: true,
    });
    const blob: Blob = await new Promise((res) =>
      canvas.toBlob((b) => res(b ?? new Blob()), "image/png", 0.8),
    );
    if (!blob.size) return null;
    const { url, key } = await presign(apiBase, appKey, "image/png");
    const up = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "image/png" },
      body: blob,
    });
    if (!up.ok) return null;
    return key;
  } catch {
    return null;
  }
}
